import {
    Button,
    Container,
    Typography,
    Tooltip,
    Stack,
    Paper,
    Box
} from "@mui/material";
import { useContext, useState } from 'react';
import { MyAppBar } from "../components/Home/MyAppBar/myAppBar";
import AddIcon from '@mui/icons-material/Add';
import { MyProjectsList } from "../components/Project/myProjectsList";
import { AuthContext } from '../contexts/authContext';
import { NewProjectDialog } from "../components/Dialogs/newProjectDialog";
import { UserAppBar } from '../components/User/userAppBar';
import { LoadingCircle } from '../components/loading';
import { USER_TYPES } from '../constants/userTypes';
import { useQuery } from "@tanstack/react-query";
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';

export const MyProjects = () => {
    const { user, userProfile, profileLoading } = useContext(AuthContext);
    const [newProjectDialogOpen, setNewProjectDialogOpen] = useState(false);

    // Fetch projects to check count
    const { data: projects, isLoading: projectsLoading } = useQuery({
        queryKey: ["loadmyprojects"],
        queryFn: async () => {
            const q = query(collection(db, "project"), where("owner", "==", user?.uid));
            const data = await getDocs(q);
            const filteredData = data.docs.map((doc) => ({
                ...doc.data(),
                id: doc.id
            }));
            return filteredData;
        },
        enabled: !!user?.uid
    });

    const handleCloseNewProjectDialog = () => {
        setNewProjectDialogOpen(false);
    }

    const handleNewProject = () => {
        setNewProjectDialogOpen(true);
    }

    // Check if user is FREE and has reached the limit
    const isFreeUser = userProfile?.type === USER_TYPES.FREE;
    const projectCount = projects?.length || 0;
    const maxProjectsForFree = 3;
    const isAtLimit = isFreeUser && projectCount >= maxProjectsForFree;
    const isButtonDisabled = isAtLimit || projectsLoading;

    if (profileLoading) {
        return (
            <>
                <MyAppBar />
                <Container maxWidth="md">
                    <LoadingCircle />
                </Container>
            </>
        );
    }

    if (!userProfile) {
        return (
            <>
                <MyAppBar />
                <Container maxWidth="md">
                    <h1>No profile data found</h1>
                </Container>
            </>
        );
    }

    return (
        <>
            <UserAppBar userData={userProfile} />
            <Container
                maxWidth="md"
                sx={{ pt: { xs: 4, sm: 6 }, pb: { xs: 6, sm: 8 } }}
            >
                <Stack spacing={4}>
                    <Stack spacing={1.5}>
                        <Typography variant="h4">
                            My Projects
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Manage your existing workspaces or start a new project to explore fresh ideas.
                        </Typography>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.5}
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            justifyContent="flex-start"
                        >
                            <Tooltip
                                title={isAtLimit ? `Free users can create up to ${maxProjectsForFree} projects. Please upgrade to create more projects.` : ""}
                                arrow
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<AddIcon />}
                                        onClick={handleNewProject}
                                        disabled={isButtonDisabled}
                                    >
                                        New Project
                                    </Button>
                                </span>
                            </Tooltip>
                            <Typography variant="body2" color="text.secondary">
                                {projectsLoading
                                    ? "Checking your projects…"
                                    : `You have created ${projectCount} of ${maxProjectsForFree} projects available on the free plan.`}
                            </Typography>
                        </Stack>
                    </Stack>

                    <Paper
                        elevation={0}
                        sx={{
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            overflow: 'hidden'
                        }}
                    >
                        <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                            <MyProjectsList user={user} />
                        </Box>
                    </Paper>
                </Stack>
                <NewProjectDialog
                    open={newProjectDialogOpen}
                    type={1}
                    handleClose={handleCloseNewProjectDialog}
                    uid={user.uid}
                />
            </Container>
        </>
    );
};